import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Phone, ArrowsClockwise, CheckCircle, Code } from "@phosphor-icons/react";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "vapi", name: "Vapi", note: "~$0.05–$0.10/min · dev-friendly · GPT/Claude under the hood" },
  { id: "retell", name: "Retell AI", note: "~$0.07/min · lower latency · polished demos" },
  { id: "elevenlabs", name: "ElevenLabs", note: "~$0.08/min · best-in-class voice quality" },
  { id: "mock", name: "Mock (demo)", note: "Simulated calls only — for previewing the UI" },
];

export default function VoiceAgent() {
  const { activeBiz } = useOutletContext();
  const [cfg, setCfg] = useState(null);
  const [calls, setCalls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openCall, setOpenCall] = useState(null);

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const [a, b] = await Promise.all([
        api.get(`/businesses/${activeBiz.id}/voice-agent`),
        api.get(`/businesses/${activeBiz.id}/voice-calls`),
      ]);
      setCfg(a.data.config);
      setCalls(b.data);
    } catch {}
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;
  if (!cfg) return <div className="p-12"><span className="spinner text-zinc-400"></span></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/businesses/${activeBiz.id}/voice-agent`, cfg);
      toast.success("Voice agent saved");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const simulate = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${activeBiz.id}/voice-calls/simulate`);
      toast.success("Simulated call added");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tiny mb-1 flex items-center gap-2"><Phone size={14} weight="bold" /> AI Voice Receptionist</div>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="va-title">Donna 2.0 — your 24/7 receptionist</h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">Picks up missed calls, qualifies the caller, books appointments, warm-transfers hot leads to your phone. Pricing add-on: $197–$497/mo.</p>
        </div>
        <button className="btn-secondary !py-2 !text-[11px]" onClick={simulate} disabled={busy} data-testid="va-simulate">Simulate call</button>
      </header>

      {(!cfg.provider || cfg.provider === "mock") && (
        <div className="bento p-5 border-l-4 border-[#FACC15] bg-yellow-50 text-sm" data-testid="va-mock-warning">
          <strong>Mock mode.</strong> Pick a real voice-AI provider and add API keys to <code className="font-mono">/app/backend/.env</code> to take real calls. The UI and call log are fully wired and will activate the moment a key arrives.
        </div>
      )}

      <div className="bento p-6 space-y-4">
        <h2 className="font-display text-xl font-bold">Provider</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              onClick={() => setCfg({ ...cfg, provider: p.id })}
              className={`p-4 text-left border-2 transition-colors ${cfg.provider === p.id ? "border-[#002FA7] bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
              data-testid={`va-provider-${p.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-bold">{p.name}</span>
                {cfg.provider === p.id && <CheckCircle size={16} weight="fill" className="text-[#002FA7]" />}
              </div>
              <p className="text-xs text-zinc-600 mt-1">{p.note}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="bento p-6 space-y-4">
        <h2 className="font-display text-xl font-bold">Agent settings</h2>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-bold">Enabled</div>
            <div className="text-xs text-zinc-500">When on, the agent will accept inbound calls (real provider required).</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="va-enable-toggle" />
            <div className="w-11 h-6 bg-zinc-200 peer-checked:bg-[#002FA7] transition-colors relative">
              <div className={`absolute top-0.5 ${cfg.enabled ? "left-5" : "left-0.5"} bg-white w-5 h-5 transition-all`}></div>
            </div>
          </label>
        </div>
        <Field label="Greeting"><textarea className="input-field" rows={2} value={cfg.greeting || ""} onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })} data-testid="va-greeting" /></Field>
        <Field label="Qualifying questions (one per line)">
          <textarea className="input-field font-mono text-xs" rows={4} value={(cfg.qualifying_questions || []).join("\n")} onChange={(e) => setCfg({ ...cfg, qualifying_questions: e.target.value.split("\n").filter(Boolean) })} data-testid="va-questions" />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Transfer phone (hot leads)"><input className="input-field" placeholder="+15551234567" value={cfg.transfer_phone || ""} onChange={(e) => setCfg({ ...cfg, transfer_phone: e.target.value })} data-testid="va-transfer" /></Field>
          <Field label="Business hours"><input className="input-field" value={cfg.business_hours || ""} onChange={(e) => setCfg({ ...cfg, business_hours: e.target.value })} data-testid="va-hours" /></Field>
        </div>
        <Field label="Knowledge base / FAQ (free text)">
          <textarea className="input-field font-mono text-xs" rows={5} value={cfg.knowledge_base || ""} onChange={(e) => setCfg({ ...cfg, knowledge_base: e.target.value })} data-testid="va-kb" />
        </Field>
        <button className="btn-primary" onClick={save} disabled={busy} data-testid="va-save">Save agent</button>
      </div>

      <div className="bento p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Call log</h2>
          <button className="btn-ghost text-xs" onClick={reload} data-testid="va-refresh"><ArrowsClockwise size={12} weight="bold" /> Refresh</button>
        </div>
        {calls.length === 0 ? (
          <p className="text-zinc-500 text-sm py-6 text-center">No calls yet. Use "Simulate call" to preview.</p>
        ) : (
          <div className="space-y-2">
            {calls.map((c) => (
              <div key={c.id} className="border border-zinc-200" data-testid={`va-call-${c.id}`}>
                <button onClick={() => setOpenCall(openCall === c.id ? null : c.id)} className="w-full p-4 text-left flex flex-wrap items-center gap-4 hover:bg-zinc-50">
                  <Phone size={16} className="text-[#002FA7]" />
                  <div className="flex-1">
                    <div className="font-mono text-sm">{c.from_number}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{c.intent}</div>
                  </div>
                  <span className="font-mono text-xs text-zinc-500">{Math.floor(c.duration_sec / 60)}m {c.duration_sec % 60}s</span>
                  <span className={`label-tiny ${c.lead_score >= 70 ? "text-[#22C55E]" : c.lead_score >= 50 ? "text-[#EAB308]" : "text-zinc-500"}`}>Score {c.lead_score}</span>
                  {c.transferred && <span className="label-tiny text-[#002FA7]">→ TRANSFERRED</span>}
                  {c.mocked && <span className="label-tiny text-zinc-500">MOCK</span>}
                </button>
                {openCall === c.id && (
                  <div className="border-t border-zinc-200 p-4 bg-zinc-50 space-y-2">
                    {(c.transcript || []).map((t, i) => (
                      <div key={i} className="flex gap-3 text-sm">
                        <span className={`label-tiny w-16 shrink-0 ${t.role === "agent" ? "text-[#002FA7]" : "text-zinc-700"}`}>{t.role}</span>
                        <span className="font-mono">{t.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block space-y-2"><span className="label-tiny block">{label}</span>{children}</label>;
}
